const loadingScripts = new Map<string, Promise<void>>()

/**
 * 动态加载外部 script（同一 URL 只加载一次）。
 *
 * 业务流程：
 * 1. 已存在相同 src 的 script 则直接 resolve
 * 2. 已有进行中的加载 Promise 则复用
 * 3. 创建 script 标签插入 head，成功/失败后更新缓存
 */
export function loadScript(src: string, attrs?: Record<string, string>): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('document unavailable'))
  }

  const existing = document.querySelector(`script[src="${src}"]`)
  if (existing) {
    return Promise.resolve()
  }

  const inflight = loadingScripts.get(src)
  if (inflight) {
    return inflight
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    if (attrs) {
      Object.keys(attrs).forEach((key) => {
        script.setAttribute(key, attrs[key])
      })
    }
    script.onload = () => resolve()
    script.onerror = () => {
      loadingScripts.delete(src)
      reject(new Error(`Failed to load script: ${src}`))
    }
    document.head.appendChild(script)
  })

  loadingScripts.set(src, promise)
  return promise
}

/** Nuvei / SafeCharge Web SDK */
export const NUVEI_CHECKOUT_SCRIPT =
  'https://cdn.safecharge.com/safecharge_resources/v1/checkout/checkout.js'

/** Checkout.com Web Components */
export const CHECKOUT_WEB_COMPONENTS_SCRIPT =
  'https://checkout-web-components.checkout.com/index.js'

/**
 * 按需加载 Nuvei SDK，返回全局 checkout 方法。
 */
export async function ensureNuveiCheckoutReady(): Promise<any> {
  await loadScript(NUVEI_CHECKOUT_SCRIPT, { defer: 'true' })
  // @ts-ignore
  const fn = window.checkout
  if (typeof fn !== 'function') {
    throw new Error('Nuvei checkout SDK not available after load')
  }
  return fn
}

/**
 * 按需加载 Checkout.com SDK，返回全局 CheckoutWebComponents。
 */
export async function ensureCheckoutWebComponentsReady(): Promise<any> {
  await loadScript(CHECKOUT_WEB_COMPONENTS_SCRIPT, { defer: 'true' })
  // @ts-ignore
  const fn = window.CheckoutWebComponents
  if (typeof fn !== 'function') {
    throw new Error('CheckoutWebComponents SDK not available after load')
  }
  return fn
}
